import {
  ArrowCta,
  Button,
  FormCard,
  Heading,
  HStack,
  Text,
} from 'components/ui';
import { X } from 'lucide-react';
import { CommonQuestions } from './CommonQuestions';
import { CostBand } from './CostBand';
import { VisitHeader } from './VisitHeader';
import { DateTimePicker } from './DateTimePicker';
import HeaderNav from '../HeaderNav/HeaderNav';
import { Navigate } from 'react-router-dom';
import { useScheduleAppointmentPage } from './useScheduleAppointmentPage';
import { IconButton } from '../Form/IconButton';
import { LoadingScreen } from '../ui/LoadingScreen';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';
import { AvailabilityType } from './useFetchAvailability';
import { getCareVariant, getVisitCopy } from './useSchedulerFaq';

export const ScheduleAppointment = () => {
  const {
    affiliate,
    availabilityRecords,
    availabilityType,
    createAppointmentError,
    dismissCreateAppointmentError,
    firstAvailableDay,
    isCreatingNewAppointment,
    isFetchingAvailability,
    isInitiallyFetchingAvailability,
    isStripe,
    onCreateMeeting,
    onNavigateMonth,
    onSelectDate,
    onSelectTime,
    onSkip,
    selectedDate,
    selectedTime,
    userIdQueryParam,
    timezone,
  } = useScheduleAppointmentPage();

  if (!userIdQueryParam) return <Navigate to={'/'} replace />;

  if (isInitiallyFetchingAvailability)
    return (
      <LoadingScreen
        message={'Finding availability with an Ayble Care Team Member'}
      />
    );

  const subTitle =
    availabilityType === AvailabilityType.coach
      ? 'Initial Coach Call'
      : 'Initial Provider Consultation';

  const visitCopy = getVisitCopy(getCareVariant(availabilityType));

  return (
    <StickyCtaLayout
      actionArea={
        <>
          <ArrowCta
            disabled={!selectedTime}
            loading={isCreatingNewAppointment}
            onClick={onCreateMeeting}
          >
            Book Appointment
          </ArrowCta>

          <Button
            bgColor={'transparent'}
            onClick={onSkip}
            variant={'ghost'}
            h={'auto'}
            minH={'auto'}
            py={0}
            w={'100%'}
            mt={4}
          >
            <Text
              color={'secondary.500'}
              fontSize={'14px'}
              fontWeight={600}
              textDecoration={'underline'}
            >
              Skip for now
            </Text>
          </Button>
        </>
      }
      header={<HeaderNav />}
      maxWidth="600px"
    >
      <Heading
        color={'secondary.500'}
        textStyle="heading.h2"
        textAlign="center"
      >
        Schedule your first visit
      </Heading>

      {createAppointmentError && (
        <HStack
          alignItems={'center'}
          justifyContent={'space-between'}
          borderColor={'red.500'}
          borderWidth={1}
          backgroundColor={'red.100'}
          mb={6}
          p={3}
        >
          <Text fontWeight="bold" color={'red.500'}>
            {createAppointmentError}
          </Text>

          <IconButton onClick={dismissCreateAppointmentError}>
            <X size={16} />
          </IconButton>
        </HStack>
      )}

      <FormCard mt={'22px'}>
        <CostBand header={affiliate?.cost_transparency_header} />

        <VisitHeader
          description={visitCopy.providerLine}
          duration={visitCopy.duration}
          title={subTitle}
        />

        <DateTimePicker
          availability={availabilityRecords}
          firstAvailableDay={firstAvailableDay}
          isFetchingAvailability={isFetchingAvailability}
          onNavigateMonth={onNavigateMonth}
          onSelectDate={onSelectDate}
          onSelectTime={onSelectTime}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          timezone={timezone}
        />
      </FormCard>

      <CommonQuestions
        affiliate={affiliate}
        availabilityType={availabilityType}
        isStripe={isStripe}
      />
    </StickyCtaLayout>
  );
};
